import type { CaseFormValues } from "@/components/Forms/CaseForm.tsx";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { invalidateCaseQueries } from "@/utils/cacheUtils.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";

export const useSaveCaseMutation = (): UseMutationResult<
    void,
    Error,
    { caseUUID: string; foundCaseId?: string } & CaseFormValues
> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ caseUUID, foundCaseId, ...attributes }) => {
            const response = await fetch(apiUrl(`/case/save/${caseUUID}`), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    data: {
                        id: caseUUID,
                        type: "case",
                        attributes: attributes,
                    },
                }),
            });
            await handleJsonApiError(response);
        },
        onSuccess: async (_data, { caseUUID }) => {
            await queryClient.invalidateQueries({
                queryKey: ["case-tasks", caseUUID],
            });
            await queryClient.invalidateQueries({
                queryKey: ["case-notes", caseUUID],
            });

            await queryClient.invalidateQueries({
                queryKey: ["assignment-changes", caseUUID],
            });

            await invalidateCaseQueries(queryClient, caseUUID);
        },
    });
};
